import { useCallback, useRef } from 'react';

/**
 * Fixes the app-wide scroll defects tracked in issue #233 (intermittent scroll
 * jump / blank space at the bottom). The single page scroller is `.main-content`;
 * the window itself never scrolls. Two problems are handled here:
 *
 * 1. AG Grid consumes wheel events inside its own fixed-height viewport, so when
 *    the pointer is over a grid the wheel never reaches `.main-content` and the
 *    page appears stuck / leaves a blank region (the #209 signature). We forward
 *    the leftover wheel delta up the scroll chain to `.main-content`, and suppress
 *    Safari/macOS rubber-band overscroll at the boundary.
 * 2. A control that shrinks content height (e.g. a filter toggle) can leave the
 *    scroller parked past the new content. We clamp `scrollTop` back into range
 *    whenever the content shrinks.
 *
 * This generalises the per-page handler that previously lived only in
 * HubOptimizer so every AG-grid page benefits. Returns a callback ref to attach
 * to the `.main-content` element; attachment follows the node mounting/unmounting
 * so it works even though the app shell renders conditionally (auth/login).
 */

const WHEEL_LINE_HEIGHT_PX = 16;

const normalizeWheelDeltaY = (event: WheelEvent, scroller: HTMLElement): number => {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * WHEEL_LINE_HEIGHT_PX;
  }
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return event.deltaY * scroller.clientHeight;
  }
  return event.deltaY;
};

const isVerticallyScrollable = (el: HTMLElement): boolean => {
  if (el.scrollHeight <= el.clientHeight + 1) return false;
  const overflowY = window.getComputedStyle(el).overflowY;
  return overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
};

const applyVerticalScrollDelta = (el: HTMLElement, deltaY: number): number => {
  if (deltaY === 0) return 0;
  const maxScrollTop = el.scrollHeight - el.clientHeight;
  if (maxScrollTop <= 0) return 0;
  const prev = el.scrollTop;
  const next = Math.min(maxScrollTop, Math.max(0, prev + deltaY));
  if (next === prev) return 0;
  el.scrollTop = next;
  return next - prev;
};

const clampScrollTop = (el: HTMLElement): void => {
  const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
  if (el.scrollTop > maxScrollTop) {
    el.scrollTop = maxScrollTop;
  }
};

export const useMainContentScrollFix = (): ((node: HTMLElement | null) => void) => {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((scroller: HTMLElement | null) => {
    // Detach from a previously attached node.
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!scroller) return;

    const handleWheelCapture = (event: WheelEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.shiftKey || event.deltaY === 0) return;

      const rawTarget = event.target;
      const eventTarget =
        rawTarget instanceof Element
          ? rawTarget
          : rawTarget instanceof Node
            ? rawTarget.parentElement
            : null;
      if (!(eventTarget instanceof Element) || !scroller.contains(eventTarget)) return;
      if (!eventTarget.closest('.ag-root-wrapper')) return;
      if (eventTarget.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (!isVerticallyScrollable(scroller)) return;

      const deltaY = normalizeWheelDeltaY(event, scroller);
      if (!Number.isFinite(deltaY) || deltaY === 0) return;

      const scrollChain: HTMLElement[] = [];
      let node: Element | null = eventTarget;
      while (node) {
        if (node instanceof HTMLElement && isVerticallyScrollable(node) && !scrollChain.includes(node)) {
          scrollChain.push(node);
        }
        if (node === scroller) break;
        node = node.parentElement;
      }
      if (!scrollChain.includes(scroller)) {
        scrollChain.push(scroller);
      }

      let remainingDelta = deltaY;
      let consumed = 0;
      for (const container of scrollChain) {
        if (remainingDelta === 0) break;
        const applied = applyVerticalScrollDelta(container, remainingDelta);
        if (applied !== 0) {
          consumed += applied;
          remainingDelta -= applied;
        }
      }

      if (consumed !== 0) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Suppress Safari/macOS rubber-band overscroll when already at the boundary.
      if (scrollChain.length > 0) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Capture phase + passive:false is required because AG Grid consumes wheel events internally.
    scroller.addEventListener('wheel', handleWheelCapture, { capture: true, passive: false });

    // Clamp scrollTop when content shrinks so the viewport never strands past the
    // content and leaves a blank gap. ResizeObserver fires on the routed page's
    // size changes; the MutationObserver keeps it bound to the current child(ren).
    const resizeObserver = new ResizeObserver(() => clampScrollTop(scroller));
    const observeChildren = () => {
      for (const child of Array.from(scroller.children)) {
        resizeObserver.observe(child);
      }
    };
    observeChildren();
    const childObserver = new MutationObserver(() => {
      resizeObserver.disconnect();
      observeChildren();
    });
    childObserver.observe(scroller, { childList: true });

    cleanupRef.current = () => {
      scroller.removeEventListener('wheel', handleWheelCapture, true);
      resizeObserver.disconnect();
      childObserver.disconnect();
    };
  }, []);
};
